import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { useAction } from "convex/react";
import { CreditCard, Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  billingDowngradeToFree,
  billingUpgradeToEnterprise,
  billingUpgradeToGrowth,
  stripeSetupIntentsCreateSetupIntent,
  stripeSetupIntentsGetDefaultPaymentMethod,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { env } from "~/env";
import { logger } from "~/lib/logger";

import { ChangePlanCard } from "./change-plan-card";
import { CurrentTierCard } from "./current-tier-card";
import { PaymentMethodForm } from "./payment-method-form";
import { TIER_LABELS, type TierInfo } from "./types";

export function BillingContent({
  organization,
  tierInfo,
}: {
  organization: { id: string };
  tierInfo: TierInfo;
}) {
  const upgradeToGrowth = useAction(billingUpgradeToGrowth);
  const upgradeToEnterprise = useAction(billingUpgradeToEnterprise);
  const downgradeToFree = useAction(billingDowngradeToFree);
  const getDefaultPaymentMethod = useAction(
    stripeSetupIntentsGetDefaultPaymentMethod
  );
  const createSetupIntent = useAction(stripeSetupIntentsCreateSetupIntent);

  const [paymentMethod, setPaymentMethod] = useState<{
    id: string;
    type: string;
    card?: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
  } | null>(null);

  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(
    null
  );
  const [stripeInstance, setStripeInstance] =
    useState<Promise<Stripe | null> | null>(null);
  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState<
    "growth" | "enterprise" | null
  >(null);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [paymentMethodLoaded, setPaymentMethodLoaded] = useState(false);

  // Load payment method on first render
  if (!paymentMethodLoaded) {
    setPaymentMethodLoaded(true);
    getDefaultPaymentMethod({ organizationId: organization.id })
      .then((pm) => {
        if (pm.hasPaymentMethod && pm.paymentMethod) {
          setPaymentMethod(pm.paymentMethod);
        }
      })
      .catch((error) => {
        logger.exception("payment_method_load_failed", error);
      });
  }

  const refetchPaymentMethod = async () => {
    try {
      const pm = await getDefaultPaymentMethod({
        organizationId: organization.id,
      });
      if (pm.hasPaymentMethod && pm.paymentMethod) {
        setPaymentMethod(pm.paymentMethod);
      } else {
        setPaymentMethod(null);
      }
    } catch (error) {
      logger.exception("payment_method_refetch_failed", error);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const { clientSecret } = await createSetupIntent({
        organizationId: organization.id,
      });
      setSetupClientSecret(clientSecret);
      setStripeInstance(loadStripe(env.PUBLIC_STRIPE_PUBLISHABLE_KEY));
      setShowPaymentMethodForm(true);
    } catch (error) {
      logger.exception("setup_intent_create_failed", error);
      toast.error("Failed to initialize payment method setup");
    }
  };

  const handleUpgrade = async (tier: "growth" | "enterprise") => {
    setIsUpgrading(true);
    try {
      const result =
        tier === "growth"
          ? await upgradeToGrowth({ organizationId: organization.id })
          : await upgradeToEnterprise({ organizationId: organization.id });

      if (result.success) {
        toast.success(`Upgraded to ${TIER_LABELS[tier]} tier`);
        setShowUpgradeConfirm(null);
      } else {
        toast.error(result.error ?? "Failed to upgrade");
      }
    } catch (error) {
      logger.exception("tier_upgrade_failed", error);
      toast.error("Failed to upgrade tier");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleDowngrade = async () => {
    setIsDowngrading(true);
    try {
      const result = await downgradeToFree({
        organizationId: organization.id,
      });

      if (result.success) {
        toast.success(
          "Downgrade scheduled. Your tier will change to Free at the end of the billing period."
        );
        setShowDowngradeConfirm(false);
      } else {
        toast.error(result.error ?? "Failed to schedule downgrade");
      }
    } catch (error) {
      logger.exception("tier_downgrade_failed", error);
      toast.error("Failed to schedule downgrade");
    } finally {
      setIsDowngrading(false);
    }
  };

  const currentTier = tierInfo.tier ?? "free";

  const paymentFormElement =
    showPaymentMethodForm && setupClientSecret && stripeInstance ? (
      <div className="bg-muted/50 rounded-lg border p-6">
        <Elements
          options={{ clientSecret: setupClientSecret }}
          stripe={stripeInstance}
        >
          <PaymentMethodForm
            onCancel={() => setShowPaymentMethodForm(false)}
            onSuccess={async () => {
              setShowPaymentMethodForm(false);
              await refetchPaymentMethod();
            }}
          />
        </Elements>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-6">
      <CurrentTierCard currentTier={currentTier} tierInfo={tierInfo} />

      <ChangePlanCard
        currentTier={currentTier}
        isDowngrading={isDowngrading}
        isUpgrading={isUpgrading}
        onDowngrade={handleDowngrade}
        onUpgrade={handleUpgrade}
        setShowDowngradeConfirm={setShowDowngradeConfirm}
        setShowUpgradeConfirm={setShowUpgradeConfirm}
        showDowngradeConfirm={showDowngradeConfirm}
        showUpgradeConfirm={showUpgradeConfirm}
        tierInfo={tierInfo}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-5" />
            Payment Method
          </CardTitle>
          <CardDescription>
            Manage the payment method used for your subscription
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentMethod ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-muted flex size-12 items-center justify-center rounded-lg">
                    <CreditCard className="size-6" />
                  </div>
                  <div>
                    <p className="subtitle-sm capitalize">
                      {paymentMethod.card?.brand ?? paymentMethod.type} ending
                      in {paymentMethod.card?.last4}
                    </p>
                    {paymentMethod.card && (
                      <p className="body-sm text-muted-foreground">
                        Expires {paymentMethod.card.expMonth}/
                        {paymentMethod.card.expYear}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={handleAddPaymentMethod}
                  size="sm"
                  variant="outline"
                >
                  <RefreshCw className="mr-2 size-4" />
                  Update
                </Button>
              </div>
              {paymentFormElement}
            </div>
          ) : (
            <div className="py-8 text-center">
              <CreditCard className="text-muted-foreground/20 mx-auto mb-4 size-16" />
              <p className="subtitle-sm text-foreground mb-2">
                No Payment Method
              </p>
              <p className="body-sm text-muted-foreground mb-4">
                Add a payment method to upgrade to a paid tier
              </p>
              <Button onClick={handleAddPaymentMethod}>
                <Plus className="mr-2 size-4" />
                Add Payment Method
              </Button>
              {paymentFormElement ? (
                <div className="mt-6 text-left">{paymentFormElement}</div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
